import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, LogOut, ArrowLeft, HelpCircle, Plus, Edit, Trash2, GripVertical, Save, X, Hammer } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

import { BACKEND_URL } from '../config/api';

export default function AdminFAQManagement() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tradesperson');
  const [showModal, setShowModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'tradesperson',
    order: 0
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      
      if (response.data.user_type !== 'admin') {
        navigate('/login');
        return;
      }
      
      setUser(response.data);
      fetchFAQs();
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchFAQs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/faqs`);
      setFaqs(response.data);
    } catch (error) {
      toast.error('Failed to fetch FAQs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFaq = () => {
    setEditingFaq(null);
    setFormData({
      question: '',
      answer: '',
      category: activeTab,
      order: filteredFaqs.length
    });
    setShowModal(true);
  };

  const handleEditFaq = (faq) => {
    setEditingFaq(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      category: faq.category,
      order: faq.order || 0
    });
    setShowModal(true);
  };

  const handleSaveFaq = async () => {
    if (!formData.question.trim() || !formData.answer.trim()) {
      toast.error('Please fill in both question and answer');
      return;
    }

    try {
      if (editingFaq) {
        await axios.put(
          `${BACKEND_URL}/api/admin/faqs/${editingFaq.faq_id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('FAQ updated successfully');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/admin/faqs`,
          formData,
          { withCredentials: true }
        );
        toast.success('FAQ created successfully');
      }
      setShowModal(false);
      fetchFAQs();
    } catch (error) {
      toast.error('Failed to save FAQ');
    }
  };

  const handleDeleteFaq = async (faq) => {
    if (!window.confirm(`Are you sure you want to delete this FAQ?\n\n"${faq.question}"`)) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/faqs/${faq.faq_id}`, {
        withCredentials: true
      });
      toast.success('FAQ deleted successfully');
      fetchFAQs();
    } catch (error) {
      toast.error('Failed to delete FAQ');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  const filteredFaqs = faqs.filter(faq => faq.category === activeTab);
  const tradespersonCount = faqs.filter(faq => faq.category === 'tradesperson').length;
  const employerCount = faqs.filter(faq => faq.category === 'employer').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="btn-secondary text-sm flex items-center gap-2"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
              Dashboard
            </button>
            <div className="flex items-center gap-2">
              <HelpCircle className="h-6 w-6 text-orange-500" />
              <h1 className="text-xl font-bold text-slate-900">FAQ Management</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">Hi, {user?.name}</span>
            <button
              onClick={handleLogout}
              className="btn-secondary text-sm flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('tradesperson')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === 'tradesperson'
                  ? 'bg-orange-500 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
              data-testid="tab-tradesperson"
            >
              <Hammer className="h-4 w-4" />
              Tradespeople ({tradespersonCount})
            </button>
            <button
              onClick={() => setActiveTab('employer')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === 'employer'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
              data-testid="tab-employer"
            >
              <Building2 className="h-4 w-4" />
              Employers ({employerCount})
            </button>
          </div>
          <button
            onClick={handleCreateFaq}
            className="btn-primary flex items-center gap-2"
            data-testid="add-faq-btn"
          >
            <Plus className="h-4 w-4" />
            Add FAQ
          </button>
        </div>

        {/* FAQ List */}
        <div className="space-y-3" data-testid="faq-list">
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <HelpCircle className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg mb-4">No FAQs for {activeTab === 'tradesperson' ? 'Tradespeople' : 'Employers'} yet.</p>
              <button
                onClick={handleCreateFaq}
                className="btn-primary inline-flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add First FAQ
              </button>
            </div>
          ) : (
            filteredFaqs.map((faq, index) => (
              <div
                key={faq.faq_id}
                className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
                data-testid={`faq-item-${faq.faq_id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="text-slate-400 pt-1">
                    <GripVertical className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <span className="text-xs text-slate-400 font-medium">Q{index + 1}</span>
                        <h3 className="font-semibold text-slate-900 mt-1">{faq.question}</h3>
                        <p className="text-slate-600 text-sm mt-2 line-clamp-2">{faq.answer}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleEditFaq(faq)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit FAQ"
                          data-testid={`edit-faq-${faq.faq_id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteFaq(faq)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete FAQ"
                          data-testid={`delete-faq-${faq.faq_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Preview Link */}
        <div className="mt-8 p-4 bg-slate-100 rounded-xl text-center">
          <p className="text-sm text-slate-600 mb-2">Preview how users will see FAQs:</p>
          <button
            onClick={() => window.open('/faq', '_blank')}
            className="text-orange-600 hover:text-orange-700 font-medium underline"
            data-testid="preview-faq-link"
          >
            Open Public FAQ Page →
          </button>
        </div>
      </div>

      {/* Create/Edit FAQ Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-orange-500" />
              {editingFaq ? 'Edit FAQ' : 'Add New FAQ'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full"
                data-testid="faq-category-select"
              >
                <option value="tradesperson">For Tradespeople</option>
                <option value="employer">For Employers</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Question *</label>
              <input
                type="text"
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                className="w-full"
                placeholder="e.g., How do I create a profile?"
                data-testid="faq-question-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Answer *</label>
              <textarea
                value={formData.answer}
                onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                className="w-full"
                rows={5}
                placeholder="Provide a detailed answer to the question..."
                data-testid="faq-answer-input"
              />
            </div>
            <div className="flex gap-2 pt-4 border-t">
              <button
                onClick={handleSaveFaq}
                className="btn-primary flex items-center gap-2"
                data-testid="save-faq-btn"
              >
                <Save className="h-4 w-4" />
                {editingFaq ? 'Update FAQ' : 'Create FAQ'}
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="btn-secondary flex items-center gap-2"
                data-testid="cancel-faq-btn"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
